import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Infinity as InfinityIcon, ArrowLeft, Crown, Share2, Flame } from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";

/**
 * EndlessReplay — public flipbook viewer for one Endless run.
 *
 * Mounted at `/endless/replay/:shareId`. Pulls the run via the public
 * `/api/endless/replay/{share_id}` endpoint (no auth required) so
 * shared links work for logged-out friends. Renders:
 *  - Header with pilot + wave + score
 *  - Hero gallery: the captured JPEG snapshots in chronological wave
 *    order. Pilots can click each thumbnail to enlarge in-place.
 *  - Stat strip (blocks destroyed, top streak, duration)
 *  - "Share Run" CTA (copies the public OG share URL) +
 *    "Play Endless" CTA back to /endless.
 *
 * When the run has no snapshots (older runs, or browsers that refused
 * `canvas.toDataURL`), we fall back to the SVG share card as the hero
 * so the page never looks empty.
 */
export default function EndlessReplay() {
  const { shareId } = useParams();
  const [run, setRun] = useState(null);
  const [err, setErr] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/endless/replay/${shareId}`)
      .then((r) => !cancelled && setRun(r.data))
      .catch((e) =>
        !cancelled &&
        setErr(e?.response?.status === 404 ? "Run not found" : "Couldn't load replay")
      );
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  const onShare = async () => {
    const origin = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const url = `${origin}/api/share/endless/${shareId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — paste anywhere!", {
        description: "Discord, Twitter & Slack render the preview card.",
      });
    } catch {
      window.prompt("Copy this share link:", url);
    }
  };

  if (err) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 max-w-3xl mx-auto px-4 pt-28 pb-16 text-center">
          <div className="font-mono-cosmic text-[11px] tracking-[0.35em] text-[#ff007f] uppercase mb-3">
            // {err}
          </div>
          <Link
            to="/endless"
            className="inline-flex items-center gap-2 mt-3 font-mono-cosmic text-xs tracking-[0.25em] uppercase text-[#00ffff] hover:text-white"
            data-testid="endless-replay-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Endless
          </Link>
        </main>
      </div>
    );
  }
  if (!run) return null;

  const hasSnaps = Array.isArray(run.snapshots) && run.snapshots.length > 0;
  const active = hasSnaps ? run.snapshots[activeIdx] : null;
  const durationSec = Math.round((run.duration_ms || 0) / 1000);

  // Fallback hero: the OG share card SVG. Always available because
  // every run has a `share_id` since 2026-05-22.
  const origin = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const fallbackHero = `${origin}/api/share/endless/${shareId}/image.svg`;

  return (
    <div className="relative min-h-screen" data-testid="endless-replay-page">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-16 fade-in">
        <Link
          to="/endless"
          className="inline-flex items-center gap-2 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/55 hover:text-[#00ffff] mb-4"
          data-testid="endless-replay-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> Back to Endless
        </Link>

        <header className="mb-6 sm:mb-8">
          <div className="flex items-center gap-2 font-mono-cosmic text-[10px] sm:text-xs tracking-[0.35em] text-[#b026ff] uppercase mb-2">
            <InfinityIcon className="w-3.5 h-3.5" /> // ENDLESS REPLAY
          </div>
          <h1
            className="font-heading font-black uppercase text-3xl sm:text-5xl tracking-tighter leading-none truncate"
            data-testid="endless-replay-pilot"
          >
            {run.name}
          </h1>
          <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-2 font-mono-cosmic text-sm sm:text-base">
            <span className="text-white/55 tracking-[0.2em] uppercase text-xs">
              Survived
            </span>
            <span
              className="font-heading font-black text-2xl sm:text-3xl text-[#b026ff] tabular-nums"
              data-testid="endless-replay-waves"
            >
              Wave {run.waves}
            </span>
            <span className="text-white/30">·</span>
            <span className="text-white/55 tracking-[0.2em] uppercase text-xs">
              Score
            </span>
            <span
              className="font-heading font-black text-2xl sm:text-3xl text-[#ffd700] tabular-nums"
              data-testid="endless-replay-score"
            >
              {Number(run.score || 0).toLocaleString()}
            </span>
            {run.best_streak > 0 && (
              <>
                <span className="text-white/30">·</span>
                <span className="inline-flex items-center gap-1.5 text-[#ff66cc] font-bold tabular-nums">
                  <Flame className="w-4 h-4" /> ×{run.best_streak}
                </span>
              </>
            )}
          </div>
        </header>

        {/* Hero gallery */}
        <section
          className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 mb-10"
          data-testid="endless-replay-gallery"
        >
          <div className="glass border border-white/10 p-2 sm:p-3 relative overflow-hidden">
            {hasSnaps ? (
              <>
                <img
                  src={active.image}
                  alt={`Wave ${active.wave} snapshot`}
                  className="w-full h-auto object-contain rounded-sm"
                  style={{
                    aspectRatio: "16 / 10",
                    background: "#0a0a1e",
                  }}
                  data-testid="endless-replay-active-frame"
                />
                <div className="absolute top-4 left-4 px-2 py-1 bg-black/65 border border-[#00ffff]/40 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#00ffff]">
                  WAVE {active.wave} · {active.score.toLocaleString()} PTS
                </div>
              </>
            ) : (
              <>
                <img
                  src={fallbackHero}
                  alt="Run share card"
                  className="w-full h-auto object-contain rounded-sm"
                  style={{ aspectRatio: "1200 / 630", background: "#0a0a1e" }}
                  data-testid="endless-replay-fallback-hero"
                />
                <div className="absolute top-4 left-4 px-2 py-1 bg-black/65 border border-white/20 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/65">
                  // NO FLIPBOOK CAPTURED
                </div>
              </>
            )}
          </div>

          {/* Stat sidebar */}
          <aside
            className="glass p-4 border border-white/10 space-y-3"
            data-testid="endless-replay-stats"
          >
            <StatRow label="Waves Cleared" value={run.waves} accent="#b026ff" />
            <StatRow
              label="Final Score"
              value={Number(run.score || 0).toLocaleString()}
              accent="#ffd700"
            />
            <StatRow
              label="Blocks Destroyed"
              value={Number(run.blocks_destroyed || 0).toLocaleString()}
              accent="#00ffff"
            />
            <StatRow
              label="Top Streak"
              value={run.best_streak > 0 ? `×${run.best_streak}` : "—"}
              accent="#ff66cc"
            />
            <StatRow
              label="Duration"
              value={
                durationSec >= 60
                  ? `${Math.floor(durationSec / 60)}m ${durationSec % 60}s`
                  : `${durationSec}s`
              }
              accent="#e8e8ef"
            />
            <div className="pt-2 flex flex-col gap-2">
              <button
                type="button"
                onClick={onShare}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-[#b026ff]/50 text-[#b026ff] hover:bg-[#b026ff]/10 transition-colors font-mono-cosmic text-[11px] tracking-[0.25em] uppercase"
                data-testid="endless-replay-share-btn"
              >
                <Share2 className="w-3.5 h-3.5" /> Share Run
              </button>
              <Link
                to="/endless"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-[#00ffff]/50 text-[#00ffff] hover:bg-[#00ffff]/10 transition-colors font-mono-cosmic text-[11px] tracking-[0.25em] uppercase"
                data-testid="endless-replay-play-btn"
              >
                <Crown className="w-3.5 h-3.5" /> Play Endless
              </Link>
            </div>
          </aside>
        </section>

        {/* Flipbook thumbnails — only when more than one snapshot. */}
        {hasSnaps && run.snapshots.length > 1 && (
          <section
            className="mb-12"
            data-testid="endless-replay-thumbs"
          >
            <div className="font-mono-cosmic text-[11px] tracking-[0.35em] text-white/55 uppercase mb-3">
              // Flipbook · {run.snapshots.length} frames
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {run.snapshots.map((s, i) => (
                <button
                  type="button"
                  key={`${s.wave}-${i}`}
                  onClick={() => setActiveIdx(i)}
                  className={`relative border transition ${
                    i === activeIdx
                      ? "border-[#00ffff] shadow-[0_0_18px_rgba(0,255,255,0.35)]"
                      : "border-white/10 hover:border-white/30"
                  }`}
                  data-testid={`endless-replay-thumb-${i}`}
                  aria-label={`Wave ${s.wave} snapshot`}
                >
                  <img
                    src={s.image}
                    alt={`Wave ${s.wave}`}
                    className="block w-full h-auto"
                    style={{ aspectRatio: "16 / 10" }}
                  />
                  <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/70 font-mono-cosmic text-[9px] tracking-[0.2em] uppercase text-[#00ffff]">
                    W{s.wave}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function StatRow({ label, value, accent }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/55">
        {label}
      </span>
      <span
        className="font-heading font-black text-base tabular-nums"
        style={{ color: accent }}
      >
        {value}
      </span>
    </div>
  );
}
